import { useMemo, useState, useEffect } from "react";
import { Select, Tag, Tooltip, Dropdown, Button, message, Popconfirm } from "antd";
import type { MenuProps } from "antd";
import { FileTextOutlined, CheckCircleOutlined, ExclamationCircleOutlined, MoreOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import DateRangeFilter from "../../components/dateRangeFilter";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import StatCard from "../../components/dashboards/StatCard";
import AddButton from "../../components/addButton";
import BadgeCheck from "../../components/badgeCheck";
import { useGetAllVendorTransactionsQuery, useVerifyVendorTransactionMutation } from "../../services/vendor";
import { useGetMerchantCategoriesQuery } from "../../services/merchant";
import { useGetEmployeeDropdownQuery } from "../../services/wallet";
import usePermissions from "../../hooks/usePermissions";
import { VendorTransactionsRoute, AddExpenseRoute, EditExpenseRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface ExpenseRecord {
  key:            string;
  dateTime?:      string;
  vendorId?:      string;
  vendorName?:    string;
  description?:   string;
  amount?:        number;
  paidAmount?:    number;
  dueAmount?:     number;
  paymentType?:   string;
  paidVia?:       string;
  paymentMode?:   string;
  bill?:          string;
  paymentBill?:   string;
  employeeName?:  string;
  branchNames?:   string;
  branchIds?:     string[];
  category?:      string;
  isVerified?:    boolean;
}

interface DateRangeValue { start: Date; end: Date; }

/* ─── Constants ──────────────────────────────────────────────────────── */

// Matches the "This Month" preset in DateRangeFilter — used as the default
// so the page opens already scoped instead of showing all-time expenses.
const defaultDateRange = (): DateRangeValue => ({
  start: dayjs().startOf('month').toDate(),
  end:   dayjs().endOf('month').toDate(),
});

const AMOUNT_COLOR = '#52c41a';
const DUE_COLOR    = '#ff4d4f';

const renderAmount = (v?: number, color?: string) =>
  v === undefined || v === null ? '-' : <span style={{ color }}>₹{v.toLocaleString('en-IN')}</span>;

const renderDateTime = (dateTime?: string) => {
  if (!dateTime) return '-';
  const spaceIdx = dateTime.indexOf(' ');
  const datePart = spaceIdx === -1 ? dateTime : dateTime.slice(0, spaceIdx);
  const timePart = spaceIdx === -1 ? '' : dateTime.slice(spaceIdx + 1);
  const dt = new Date(datePart);
  const dateLabel = isNaN(dt.getTime())
    ? datePart
    : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div>
      <div>{dateLabel}</div>
      {timePart && <div style={{ fontSize: 12, opacity: 0.65 }}>{timePart}</div>}
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────── */

const ExpenseHistory = () => {
  const navigate = useNavigate();
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(10);
  const [dateRange,  setDateRange]  = useState<DateRangeValue | null>(defaultDateRange);
  const [employeeId, setEmployeeId] = useState("all");
  const [category,   setCategory]   = useState("all");

  // Branch comes from the global header branch select, not a local filter.
  const globalBranch = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof globalBranch === 'object' ? (globalBranch?._id || globalBranch?.id) : globalBranch;
  const branchId = selectedBranchId && selectedBranchId !== 'all' ? selectedBranchId : undefined;

  useEffect(() => { setPage(1); }, [selectedBranchId]);

  const { hasPermission, permissionsLoaded } = usePermissions();
  const canViewEmployeeDropdown = permissionsLoaded && hasPermission('EMPLOYEE_DROPDOWN_READ');
  const canEditExpense = permissionsLoaded && hasPermission('EXPENSE_HISTORY_UPDATE');
  const canVerifyExpense = permissionsLoaded && hasPermission('EXPENSE_HISTORY_VERIFY');

  const [verifyVendorTransaction] = useVerifyVendorTransactionMutation();
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const handleVerify = async (record: ExpenseRecord) => {
    setVerifyingId(record.key);
    try {
      await verifyVendorTransaction({ id: record.key, isVerified: true }).unwrap();
      // message.success('Marked as verified');
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setVerifyingId(null);
    }
  };

  const { data: employeeDropdownData } = useGetEmployeeDropdownQuery(undefined, { skip: !canViewEmployeeDropdown });
  // createdBy on vendor transactions is a User reference, not the Employee
  // document id — pass the linked userId, matching the same distinction
  // already used for collectedBy in Add Cash.
  const employeeOptions = [
    { value: 'all', label: 'All Employees' },
    ...((employeeDropdownData as any)?.data || []).map((e: any) => ({ value: e.userId || e._id, label: e.name || 'Unknown' })),
  ];

  const { data: categoriesResp } = useGetMerchantCategoriesQuery();
  const categoryList = Array.isArray(categoriesResp)
    ? categoriesResp
    : (Array.isArray((categoriesResp as any)?.data) ? (categoriesResp as any).data : []);
  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categoryList.map((c: any) => (typeof c === 'string' ? { value: c, label: c } : { value: c.name ?? c._id, label: c.name ?? c })),
  ];

  const { data: expensesResp, isFetching } = useGetAllVendorTransactionsQuery({
    startDate: dateRange?.start ? dayjs(dateRange.start).format('YYYY-MM-DD') : undefined,
    endDate:   dateRange?.end   ? dayjs(dateRange.end).format('YYYY-MM-DD')   : undefined,
    createdBy: employeeId !== 'all' ? employeeId : undefined,
    branchId,
    category:  category   !== 'all' ? category   : undefined,
    page,
    limit,
  });

  const apiData = expensesResp as any;

  const expenses = useMemo<ExpenseRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((e: any) => ({
      key:            e._id,
      dateTime:       e.transactionDateFormatted || e.transactionDate || e.dateTime || e.createdAt,
      vendorId:       e.merchantId?._id || e.vendor?._id,
      vendorName:     e.merchantId?.name || e.vendor?.name,
      description:    e.description,
      amount:         e.amount,
      paidAmount:     e.paidAmount,
      dueAmount:      e.dueAmount,
      paymentType:    e.paymentType,
      paidVia:        e.paidVia,
      paymentMode:    e.paymentMode,
      bill:           e.bill,
      paymentBill:    e.paymentBill,
      employeeName:   e.createdBy?.name,
      branchNames:    Array.isArray(e.branchIds)
        ? e.branchIds.map((b: any) => b?.name).filter(Boolean).join(', ')
        : (e.branch?.name || e.branchId?.name || e.createdBy?.branch?.name || e.createdBy?.branchIds?.[0]?.name),
      branchIds:      Array.isArray(e.branchIds) ? e.branchIds.map((b: any) => b?._id).filter(Boolean) : [],
      category:       e.merchantId?.category || e.vendor?.category || e.category,
      isVerified:     e.isVerified,
    }));
  }, [apiData]);

  const columns = [
    { title: 'Date',   dataIndex: 'dateTime',   key: 'dateTime',   render: renderDateTime },
    { title: 'Merchant', dataIndex: 'vendorName', key: 'vendorName',
      render: (name: string, record: ExpenseRecord) => name ? (
        <span
          style={{ color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate(`${VendorTransactionsRoute}?merchantId=${record.vendorId}`, { state: { vendorName: name } })}
        >
          {name}
        </span>
      ) : '-' },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v?: string) => v || '-' },
    { title: 'Amount', dataIndex: 'amount',     key: 'amount',     render: (v?: number) => renderAmount(v) },
    { title: 'Paid',   dataIndex: 'paidAmount', key: 'paidAmount', render: (v?: number) => renderAmount(v, AMOUNT_COLOR) },
    { title: 'Due',    dataIndex: 'dueAmount',  key: 'dueAmount',  render: (v?: number) => renderAmount(v, v ? DUE_COLOR : undefined) },
    { title: 'Payment Type', dataIndex: 'paymentType', key: 'paymentType', align: 'center' as const,
      render: (v?: string) => v ? <Tag color={v === 'full' ? 'green' : 'orange'}>{v.toUpperCase()}</Tag> : '-' },
    { title: 'Paid Via', dataIndex: 'paidVia', key: 'paidVia', align: 'center' as const,
      render: (v?: string) => v ? <Tag color={v === 'wallet' ? 'blue' : 'default'}>{v.toUpperCase()}</Tag> : '-' },
    { title: 'Payment Mode', dataIndex: 'paymentMode', key: 'paymentMode', align: 'center' as const,
      render: (v?: string, record?: ExpenseRecord) => {
        if (!v) return '-';
        const tag = <Tag color={v === 'online' ? 'purple' : 'default'}>{v.toUpperCase()}</Tag>;
        if (v !== 'online' || !record?.paymentBill) return tag;
        return (
          <Tooltip
            title={
              <div style={{ textAlign: 'center' }}>
                <img src={record.paymentBill} alt="Payment proof" style={{ maxWidth: 160, maxHeight: 160, borderRadius: 6, display: 'block', marginBottom: 6 }} />
                <a href={record.paymentBill} target="_blank" rel="noreferrer">View</a>
              </div>
            }
          >
            {tag}
          </Tooltip>
        );
      } },
    { title: 'Bill', dataIndex: 'bill', key: 'bill', align: 'center' as const,
      render: (v?: string, record?: ExpenseRecord) => {
        if (!v) return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <a href={v} target="_blank" rel="noreferrer">View</a>
            {record?.isVerified ? (
              <Tooltip title="Verified">
                <span style={{ display: 'inline-flex' }}>
                  <BadgeCheck size={16} verified />
                </span>
              </Tooltip>
            ) : canVerifyExpense && record && (
              <Popconfirm
                title="Mark this expense as verified?"
                onConfirm={() => handleVerify(record)}
                okText="Verify"
                cancelText="Cancel"
              >
                <Tooltip title="Click to verify">
                  <span
                    style={{
                      cursor: verifyingId === record.key ? 'wait' : 'pointer',
                      opacity: verifyingId === record.key ? 0.5 : 1,
                      display: 'inline-flex',
                    }}
                  >
                    <BadgeCheck size={16} verified={false} />
                  </span>
                </Tooltip>
              </Popconfirm>
            )}
          </span>
        );
      } },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v?: string) => v || '-' },
    { title: 'Employee', dataIndex: 'employeeName', key: 'employeeName', render: (v?: string) => v || '-' },
    { title: 'Branch',   dataIndex: 'branchNames', key: 'branchNames', render: (v?: string) => v || '-' },
    ...(canEditExpense ? [{
      title: 'Actions', key: 'actions', align: 'center' as const,
      render: (_: unknown, record: ExpenseRecord) => {
        const items: MenuProps['items'] = [
          { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => navigate(`${EditExpenseRoute}/${record.key}`, { state: { expense: record } }) },
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
          </Dropdown>
        );
      },
    }] : []),
  ];

  const totalBillAmount = apiData?.totalBillAmount ?? apiData?.summary?.totalBillAmount ?? 0;
  const totalPaid       = apiData?.totalPaid       ?? apiData?.summary?.totalPaid       ?? 0;
  const totalDue        = apiData?.totalDue        ?? apiData?.summary?.totalDue        ?? 0;

  return (
    <div className="expense-history-page">
      <div className="header-section">
        <h2 style={{ margin: 0 }}>Expense History</h2>
        <div className="right-col">
          {canViewEmployeeDropdown && (
            <Select
              value={employeeId}
              onChange={(v: string) => { setEmployeeId(v); setPage(1); }}
              style={{ width: 160, height: 41 }}
              showSearch
              optionFilterProp="label"
              options={employeeOptions}
            />
          )}
          <Select
            value={category}
            onChange={(v: string) => { setCategory(v); setPage(1); }}
            style={{ width: 150, height: 41 }}
            showSearch
            optionFilterProp="label"
            options={categoryOptions}
          />
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => { setDateRange(v); setPage(1); }}
          />
          <AddButton to={AddExpenseRoute}>Add Expense</AddButton>
        </div>
      </div>

      <div className="expense-stats-grid">
        <StatCard title="Total Bill Amount" value={`₹${totalBillAmount.toLocaleString('en-IN')}`} icon={<FileTextOutlined />} color="#6366f1" />
        <StatCard title="Total Paid"        value={`₹${totalPaid.toLocaleString('en-IN')}`}       icon={<CheckCircleOutlined />} color="#52c41a" />
        <StatCard title="Total Due"         value={`₹${totalDue.toLocaleString('en-IN')}`}        icon={<ExclamationCircleOutlined />} color="#ff4d4f" />
      </div>

      <div className="expenses-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={expenses}
          loading={isFetching}
          pagination={false}
          rowKey={(record: ExpenseRecord) => record.key}
          scroll={{ x: 1400 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default ExpenseHistory;
